import { useState, useMemo, useEffect } from 'react';
import {
  Megaphone, Plus, Search, Calendar, BarChart2, Table as TableIcon,
  Eye, Pencil, Trash2, Send, Clock, CheckCircle2, FileText, Pin,
  X, Filter, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmModal } from '../ui/ConfirmModal';
import { ComunicadoWizardCore } from './ComunicadoWizardCore';
import { ComunicadoDetailModalCore } from './ComunicadoDetailModalCore';
import { ComunicadosCalendarView } from './ComunicadosCalendarView';
import { DESTINATARIO_LABEL } from './comunicados-shared';
import type {
  ComunicadosConfig,
  ComunicadoItem,
  ComunicadoWizardData,
} from './ComunicadosConfig';
import './comunicados-center.css';

interface Props {
  config: ComunicadosConfig;
  comunicados: ComunicadoItem[];
  isLoading: boolean;
  onCreate: (data: ComunicadoWizardData) => Promise<any>;
  onUpdate?: (id: string, data: Partial<ComunicadoWizardData>) => Promise<any>;
  onRemove: (id: string) => Promise<any>;
  onSendDraft?: (c: ComunicadoItem) => Promise<any>;
  initialDraft?: Partial<ComunicadoWizardData> | null;
}

export function ComunicadosCenterCore({
  config,
  comunicados,
  isLoading,
  onCreate,
  onUpdate,
  onRemove,
  onSendDraft,
  initialDraft,
}: Props) {
  const [viewMode, setViewMode] = useState<'tabela' | 'calendario' | 'metricas'>('tabela');
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardDraft, setWizardDraft] = useState<Partial<ComunicadoWizardData> | null>(null);
  const [editingComunicadoId, setEditingComunicadoId] = useState<string | null>(null);
  const [detailItem, setDetailItem] = useState<ComunicadoItem | null>(null);
  const [delTarget, setDelTarget] = useState<ComunicadoItem | null>(null);

  // Paginação
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Disparo direto de rascunho
  const handleSendDraft = async (c: ComunicadoItem) => {
    try {
      if (onSendDraft) {
        await onSendDraft(c);
      } else if (onUpdate) {
        await onUpdate(c.id, { status: 'ENVIADO' } as any);
      }
      toast.success(`Comunicado "${c.titulo}" disparado com sucesso!`);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Falha ao enviar comunicado.';
      toast.error(Array.isArray(msg) ? msg.join(', ') : msg);
    }
  };

  // Submissão do Wizard: se estiver editando, chama onUpdate (atualiza rascunho); se novo, onCreate
  const handleWizardSubmit = async (data: ComunicadoWizardData) => {
    if (editingComunicadoId && onUpdate) {
      await onUpdate(editingComunicadoId, data);
    } else {
      await onCreate(data);
    }
  };

  // Auto-abre wizard se receber draft (ex: vindo de redirecionamento do banco de mídias)
  useEffect(() => {
    if (initialDraft) {
      setEditingComunicadoId(null);
      setWizardDraft(initialDraft);
      setWizardOpen(true);
    }
  }, [initialDraft]);

  // Reset da página quando filtros mudarem
  useEffect(() => {
    setPage(1);
  }, [search, filterCat, filterStatus]);

  // Filtros combinados
  const filteredList = useMemo(() => {
    return comunicados.filter((c) => {
      const q = search.trim().toLowerCase();
      if (q && !c.titulo.toLowerCase().includes(q) && !c.corpo.toLowerCase().includes(q)) {
        return false;
      }
      if (filterCat && c.categoria !== filterCat) return false;
      if (filterStatus && c.status !== filterStatus) return false;
      return true;
    });
  }, [comunicados, search, filterCat, filterStatus]);

  // Lista paginada
  const totalPages = Math.max(1, Math.ceil(filteredList.length / pageSize));
  const paginatedList = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredList.slice(start, start + pageSize);
  }, [filteredList, page, pageSize]);

  // Cálculos de KPIs
  const stats = useMemo(() => {
    const total = comunicados.length;
    const enviados = comunicados.filter((c) => c.status === 'ENVIADO').length;
    const agendados = comunicados.filter((c) => c.status === 'AGENDADO').length;
    const rascunhos = comunicados.filter((c) => c.status === 'RASCUNHO').length;
    const totalVis = comunicados.reduce((acc, c) => acc + (c._count?.visualizacoes || 0), 0);
    return { total, enviados, agendados, rascunhos, totalVis };
  }, [comunicados]);

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      await onRemove(delTarget.id);
      toast.success('Comunicado removido com sucesso.');
    } catch {
      toast.error('Erro ao remover comunicado.');
    } finally {
      setDelTarget(null);
    }
  };

  const fmtDate = (d: string) => {
    try {
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return d;
    }
  };

  return (
    <div className="cmc-page animate-fadeIn">
      {/* Hero Banner */}
      <div className="cmc-hero">
        <div className="cmc-hero-circle c1" />
        <div className="cmc-hero-circle c2" />
        <div className="cmc-hero-content">
          <div>
            <h1 className="cmc-hero-title">
              <Megaphone size={22} />
              {config.labels.heroTitle}
            </h1>
            <p className="cmc-hero-subtitle">{config.labels.heroSubtitle}</p>
          </div>
          {config.features.canManage !== false && (
            <button
              type="button"
              className="mdc-btn-upload"
              onClick={() => {
                setEditingComunicadoId(null);
                setWizardDraft(null);
                setWizardOpen(true);
              }}
            >
              <Plus size={16} /> Novo Comunicado
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      {config.features.metricsCards && (
        <div className="cmc-kpi-grid">
          <div className="cmc-kpi-card">
            <div className="cmc-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
              <Megaphone size={20} />
            </div>
            <div>
              <div className="cmc-kpi-val">{stats.total}</div>
              <div className="cmc-kpi-lbl">Total de Comunicados</div>
            </div>
          </div>

          <div className="cmc-kpi-card">
            <div className="cmc-kpi-icon" style={{ background: '#ecfdf5', color: '#059669' }}>
              <CheckCircle2 size={20} />
            </div>
            <div>
              <div className="cmc-kpi-val">{stats.enviados}</div>
              <div className="cmc-kpi-lbl">Disparados com Sucesso</div>
            </div>
          </div>

          <div className="cmc-kpi-card">
            <div className="cmc-kpi-icon" style={{ background: '#fffbeb', color: '#d97706' }}>
              <Clock size={20} />
            </div>
            <div>
              <div className="cmc-kpi-val">{stats.agendados}</div>
              <div className="cmc-kpi-lbl">Envios Agendados</div>
            </div>
          </div>

          <div className="cmc-kpi-card">
            <div className="cmc-kpi-icon" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
              <Eye size={20} />
            </div>
            <div>
              <div className="cmc-kpi-val">{stats.totalVis}</div>
              <div className="cmc-kpi-lbl">Leituras Confirmadas</div>
            </div>
          </div>
        </div>
      )}

      {/* Container Principal */}
      <div className="cmc-container">
        {/* Toolbar */}
        <div className="cmc-toolbar">
          <div className="cmc-toolbar-left">
            <div className="mdc-search-box">
              <Search size={14} className="mdc-search-icon" />
              <input
                type="text"
                className="mdc-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={config.labels.searchPlaceholder || 'Buscar comunicados...'}
              />
              {search && (
                <button
                  type="button"
                  className="mdc-search-clear"
                  onClick={() => setSearch('')}
                >
                  <X size={13} />
                </button>
              )}
            </div>

            <select
              className="mdc-form-select"
              style={{ height: 36, padding: '0 8px', fontSize: '.76rem' }}
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
            >
              <option value="">Todas as Categorias</option>
              <option value="OPERACIONAL">Operacional</option>
              <option value="INSTITUCIONAL">Institucional</option>
              <option value="PEDAGOGICO">Pedagógico</option>
            </select>

            <select
              className="mdc-form-select"
              style={{ height: 36, padding: '0 8px', fontSize: '.76rem' }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">Todos os Status</option>
              <option value="ENVIADO">Enviados</option>
              <option value="AGENDADO">Agendados</option>
              <option value="RASCUNHO">Rascunhos</option>
            </select>
          </div>

          <div className="cmc-view-toggle">
            <button
              type="button"
              className={`cmc-toggle-btn ${viewMode === 'tabela' ? 'active' : ''}`}
              onClick={() => setViewMode('tabela')}
            >
              <TableIcon size={13} /> Tabela
            </button>
            {config.features.calendarView && (
              <button
                type="button"
                className={`cmc-toggle-btn ${viewMode === 'calendario' ? 'active' : ''}`}
                onClick={() => setViewMode('calendario')}
              >
                <Calendar size={13} /> Calendário
              </button>
            )}
          </div>
        </div>

        {/* Área de Conteúdo */}
        {viewMode === 'calendario' ? (
          <ComunicadosCalendarView
            comunicados={filteredList}
            config={config}
            onSelectComunicado={(c) => setDetailItem(c)}
            onCreateOnDate={(dateStr) => {
              setWizardDraft({
                titulo: '',
                corpo: '',
                tipo: 'AVISO',
                categoria: 'OPERACIONAL',
                destinatario: 'TODOS',
                status: 'ENVIADO',
                dataEvento: dateStr,
              });
              setWizardOpen(true);
            }}
          />
        ) : (
          <div className="cmc-table-wrap">
            {isLoading ? (
              <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
                Carregando comunicados...
              </div>
            ) : filteredList.length === 0 ? (
              <div className="mdc-empty">
                <div className="mdc-empty-circle">
                  <Megaphone size={36} />
                </div>
                <h3 className="mdc-empty-title">
                  {search || filterCat || filterStatus
                    ? 'Nenhum comunicado encontrado'
                    : (config.labels?.emptyTitle || 'Nenhum comunicado criado ainda')}
                </h3>
                <p className="mdc-empty-desc">
                  {search || filterCat || filterStatus
                    ? 'Nenhum comunicado corresponde aos filtros selecionados.'
                    : (config.labels?.emptySubtitle || 'Inicie a comunicação institucional e escolar enviando o primeiro aviso para as famílias e professores.')}
                </p>
                {config.features?.canManage !== false && (
                  <button
                    type="button"
                    className="mdc-btn-upload"
                    onClick={() => {
                      setWizardDraft(null);
                      setWizardOpen(true);
                    }}
                  >
                    <Plus size={15} /> Criar Primeiro Comunicado
                  </button>
                )}
              </div>
            ) : (
              <table className="cmc-table">
                <thead>
                  <tr>
                    <th style={{ width: 34 }}></th>
                    <th>Título / Assunto</th>
                    <th>Tipo & Categoria</th>
                    <th>Público Destinatário</th>
                    <th>Data de Envio</th>
                    <th>Leituras</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedList.map((c) => (
                    <tr key={c.id}>
                      <td>
                        {c.fixado && (
                          <span title="Fixado no topo">
                            <Pin size={13} color="#2563eb" fill="#2563eb" />
                          </span>
                        )}
                      </td>
                      <td>
                        <strong
                          style={{ color: 'var(--text-main)', cursor: 'pointer' }}
                          onClick={() => setDetailItem(c)}
                        >
                          {c.titulo}
                        </strong>
                        {c.configPreview?.midiaUrl && (
                          <span style={{ marginLeft: 6, fontSize: '.65rem', background: '#eff6ff', color: '#2563eb', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>
                            COM FOLDER
                          </span>
                        )}
                      </td>
                      <td>
                        <span style={{ fontSize: '.74rem', fontWeight: 600 }}>{c.tipo}</span>
                        <small style={{ display: 'block', color: 'var(--text-muted)', fontSize: '.68rem' }}>
                          {c.categoria}
                        </small>
                      </td>
                      <td>
                        <span style={{ fontSize: '.74rem', fontWeight: 600, color: '#334155' }}>
                          {DESTINATARIO_LABEL[c.destinatario] || c.destinatario}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: '.74rem', color: 'var(--text-muted)' }}>
                          {fmtDate(c.criadoEm)}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: '.74rem', fontWeight: 700, color: '#2563eb' }}>
                          {c._count?.visualizacoes || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`cmc-status-pill ${c.status.toLowerCase()}`}>
                          {c.status}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 4 }}>
                          {c.status === 'RASCUNHO' && config.features?.canManage !== false && (
                            <button
                              type="button"
                              className="mdc-action-btn"
                              title="Disparar rascunho agora"
                              style={{ color: '#16a34a' }}
                              onClick={() => void handleSendDraft(c)}
                            >
                              <Send size={14} />
                            </button>
                          )}

                          <button
                            type="button"
                            className="mdc-action-btn"
                            title="Visualizar detalhes"
                            onClick={() => setDetailItem(c)}
                          >
                            <Eye size={14} />
                          </button>

                          {config.features?.canManage !== false && (
                            <button
                              type="button"
                              className="mdc-action-btn del"
                              title="Excluir comunicado"
                              onClick={() => setDelTarget(c)}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Paginação da Tabela */}
            {filteredList.length > 0 && (
              <div className="cmc-pagination">
                <div className="cmc-page-info">
                  Mostrando <strong>{((page - 1) * pageSize) + 1}</strong> a <strong>{Math.min(page * pageSize, filteredList.length)}</strong> de <strong>{filteredList.length}</strong> comunicados
                </div>

                <div className="cmc-page-controls">
                  <span style={{ fontSize: '.74rem', color: '#64748b', marginRight: 4 }}>Exibir:</span>
                  <select
                    className="cmc-page-size-select"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    <option value={10}>10 por página</option>
                    <option value={25}>25 por página</option>
                    <option value={50}>50 por página</option>
                    <option value={100}>100 por página</option>
                  </select>

                  <button
                    type="button"
                    className="cmc-page-btn"
                    disabled={page === 1}
                    onClick={() => setPage(1)}
                    title="Primeira página"
                  >
                    <ChevronsLeft size={13} />
                  </button>
                  <button
                    type="button"
                    className="cmc-page-btn"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    title="Página anterior"
                  >
                    <ChevronLeft size={13} />
                  </button>

                  <span style={{ fontSize: '.74rem', fontWeight: 700, padding: '0 6px', color: '#1e293b' }}>
                    {page} de {totalPages}
                  </span>

                  <button
                    type="button"
                    className="cmc-page-btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    title="Próxima página"
                  >
                    <ChevronRight size={13} />
                  </button>
                  <button
                    type="button"
                    className="cmc-page-btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage(totalPages)}
                    title="Última página"
                  >
                    <ChevronsRight size={13} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Wizard Modal */}
      <ComunicadoWizardCore
        open={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
          setWizardDraft(null);
          setEditingComunicadoId(null);
        }}
        onSubmit={handleWizardSubmit}
        config={config}
        initialDraft={wizardDraft}
      />

      {/* Detail Modal */}
      <ComunicadoDetailModalCore
        comunicado={detailItem}
        config={config}
        onClose={() => setDetailItem(null)}
        onEdit={(c) => {
          setEditingComunicadoId(c.id);
          setWizardDraft({
            titulo: c.titulo,
            corpo: c.corpo,
            tipo: c.tipo,
            categoria: c.categoria,
            destinatario: c.destinatario,
            fixado: c.fixado,
            turmaId: c.turmaId || undefined,
            configPreview: c.configPreview || undefined,
          });
          setWizardOpen(true);
        }}
        onSend={handleSendDraft}
      />

      {/* Confirmação de Exclusão */}
      <ConfirmModal
        open={!!delTarget}
        title="Remover Comunicado"
        message={delTarget ? `Tem certeza que deseja remover o comunicado "${delTarget.titulo}"?` : ''}
        confirmLabel="Sim, remover"
        cancelLabel="Cancelar"
        variant="danger"
        onCancel={() => setDelTarget(null)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}
